import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ChevronLeftIcon, TrashIcon } from "@heroicons/react/24/outline";
import { User } from "../../../../../api/accessControl/User.ts";
import { BackendApi } from "../../../../../api/api.ts";
import Shell from "../../../../../components/specific/Shell.tsx";
import { PrimaryButton } from "../../../../../components/general/PrimaryButton.tsx";
import { SecondaryButton } from "../../../../../components/general/SecondaryButton.tsx";
import { fieldLabelClasses, fieldVariants } from "../../../../../components/general/fieldStyles.ts";
import Loading from "../../../../../components/general/Loading.tsx";
import { useNotifications } from "../../../../../lib/NotificationContext.tsx";
import { isAbortError } from "../../../../../lib/abortError.ts";
import { centsToLocalizedString } from "../../../../../lib/Money.ts";
import { cn } from "../../../../../lib/utils.ts";
import { CreditNoteBanner } from "../../components/salesInvoices/CreditNoteBanner.tsx";
import { backendErrorDescription } from "../../components/salesInvoices/utils/backendErrorDescription.ts";
import {
    creditSalesInvoice,
    type CreditSalesInvoiceLine,
    type SalesInvoiceResponse,
} from "../CreateInvoicePage/salesInvoiceApi.ts";
import {
    creditsEverythingThatIsLeft,
    describeRemainder,
    findUncreditableLine,
    isCreditable,
    toCreditableLines,
    totalToCreditInCents,
    type CreditableLine,
} from "./creditableLines.ts";

const MAXIMUM_CREDIT_REASON_LENGTH = 500;

export default function CreditInvoicePage({ currentUser }: { currentUser: User }) {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { dispatch } = useNotifications();
    const invoiceId = searchParams.get("invoice");

    const [invoice, setInvoice] = useState<SalesInvoiceResponse | null>(null);
    const [lines, setLines] = useState<CreditableLine[]>([]);
    const [reason, setReason] = useState("");
    const [isFetching, setIsFetching] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [fetchError, setFetchError] = useState<string | null>(null);

    const loadError = invoiceId ? fetchError : "Geen factuur opgegeven.";
    const isLoading = invoiceId !== null && isFetching;

    useEffect(() => {
        if (!invoiceId) {
            return;
        }

        const controller = new AbortController();
        const api = new BackendApi().withAbortSignal(controller.signal);
        api.Me__business__getSalesInvoice(invoiceId)
            .then((loaded) => {
                setInvoice(loaded);
                setLines(toCreditableLines(loaded));
                setIsFetching(false);
            })
            .catch((error: unknown) => {
                if (isAbortError(error)) {
                    return;
                }
                setFetchError("De factuur kon niet worden geladen.");
                setIsFetching(false);
            });

        return () => controller.abort();
    }, [invoiceId]);

    const creditableLines = useMemo(() => lines.filter(isCreditable), [lines]);
    const keptLines = useMemo(
        () => creditableLines.filter((line) => !line.removed),
        [creditableLines],
    );

    const totalCents = useMemo(() => totalToCreditInCents(keptLines), [keptLines]);
    const invalidLine = findUncreditableLine(keptLines);

    const nothingLeftToCredit = !isLoading && !loadError && creditableLines.length === 0;
    const canSubmit = keptLines.length > 0 && !invalidLine && !isSubmitting;

    function updateQuantity(lineId: string, quantity: string) {
        setLines((current) =>
            current.map((line) => (line.id === lineId ? { ...line, quantity } : line)),
        );
    }

    function toggleRemoved(lineId: string) {
        setLines((current) =>
            current.map((line) =>
                line.id === lineId ? { ...line, removed: !line.removed } : line,
            ),
        );
    }

    async function handleSubmit() {
        if (!invoice || !canSubmit) {
            return;
        }

        setIsSubmitting(true);
        const requestedLines: CreditSalesInvoiceLine[] | null =
            creditsEverythingThatIsLeft(keptLines, creditableLines)
            ? null
            : keptLines.map((line) => ({
                  original_line_id: line.id,
                  quantity: Number(line.quantity),
              }));

        try {
            const creditNote = await creditSalesInvoice(
                new BackendApi(),
                String(invoice.id),
                reason.trim() === "" ? null : reason.trim(),
                requestedLines,
            );
            dispatch({
                title: "Creditfactuur aangemaakt",
                description: "De conceptcreditfactuur staat klaar om definitief te maken.",
                type: "success",
            });
            navigate(`/invoices/${creditNote.id}`);
        } catch (error) {
            dispatch({
                title: "Creditfactuur niet aangemaakt",
                description: backendErrorDescription(
                    error,
                    "De creditfactuur kon niet worden aangemaakt.",
                ),
                type: "error",
            });
            setIsSubmitting(false);
        }
    }

    return (
        <Shell
            currentUser={currentUser}
            title={
                <div>
                    <button
                        type="button"
                        onClick={() => navigate(invoiceId ? `/invoices/${invoiceId}` : "/outstanding-sales-invoices")}
                        className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
                    >
                        <ChevronLeftIcon className="h-4 w-4" />
                        Terug naar factuur
                    </button>
                    <h1 className="mt-4 text-4xl font-normal tracking-tight text-gray-900">
                        Creditfactuur aanmaken
                    </h1>
                    {invoice?.invoice_number && (
                        <p className="mt-2 text-sm text-gray-600">
                            Crediteer (een deel van) factuur {invoice.invoice_number}
                        </p>
                    )}
                </div>
            }
        >
            {isLoading && <Loading message="Factuur laden..." />}

            {!isLoading && loadError && (
                <div className="p-6 bg-white rounded-xl shadow-sm border border-gray-100">
                    <p className="text-sm text-gray-700">{loadError}</p>
                </div>
            )}

            {!isLoading && !loadError && invoice && (
                <div className="flex flex-col gap-6 max-w-3xl">
                    <CreditNoteBanner
                        title={`Creditering van factuur ${invoice.invoice_number ?? ""}`}
                        description={
                            nothingLeftToCredit
                                ? "Deze factuur is al volledig gecrediteerd."
                                : "De regels tonen wat er nog te crediteren valt. Verwijder regels of verlaag aantallen voor een gedeeltelijke creditering."
                        }
                    />

                    <div className="p-6 bg-white rounded-xl shadow-sm border border-gray-100 flex flex-col gap-2">
                        <label htmlFor="credit-reason" className={fieldLabelClasses}>
                            Reden van creditering
                        </label>
                        <textarea
                            id="credit-reason"
                            value={reason}
                            maxLength={MAXIMUM_CREDIT_REASON_LENGTH}
                            onChange={(event) => setReason(event.target.value)}
                            rows={3}
                            disabled={nothingLeftToCredit}
                            className={cn(fieldVariants({ size: "multi" }), "px-3 py-2")}
                            placeholder="Bijvoorbeeld: dienst geannuleerd"
                        />
                    </div>

                    <div className="p-6 bg-white rounded-xl shadow-sm border border-gray-100">
                        <h3 className="text-sm font-semibold text-gray-900 mb-4">Te crediteren regels</h3>
                        <ul className="flex flex-col gap-3">
                            {lines.map((line) => (
                                <li
                                    key={line.id}
                                    className={cn(
                                        "flex items-center gap-4 rounded-lg border px-4 py-3",
                                        line.removed
                                            ? "border-gray-100 bg-gray-50 opacity-60"
                                            : "border-gray-200",
                                    )}
                                >
                                    <span className="flex-1 min-w-0">
                                        <span className="block text-sm font-medium text-gray-900 truncate">
                                            {line.description}
                                        </span>
                                        <span className="block text-xs text-gray-500">
                                            {describeRemainder(line)}
                                        </span>
                                    </span>
                                    <input
                                        type="number"
                                        min="0"
                                        step="any"
                                        max={line.remainingQuantity}
                                        disabled={line.removed || !isCreditable(line)}
                                        value={line.quantity}
                                        onChange={(event) => updateQuantity(line.id, event.target.value)}
                                        aria-label={`Aantal te crediteren voor ${line.description}`}
                                        className={cn(fieldVariants(), "w-24 text-right")}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => toggleRemoved(line.id)}
                                        disabled={!isCreditable(line)}
                                        aria-label={
                                            line.removed
                                                ? `Regel ${line.description} terugzetten`
                                                : `Regel ${line.description} verwijderen`
                                        }
                                        className="rounded-lg p-2 text-gray-400 hover:text-gray-700 disabled:opacity-40 outline-none focus-visible:border-[0.5px] focus-visible:border-[color:var(--primary)]"
                                    >
                                        <TrashIcon className="h-4 w-4" />
                                    </button>
                                </li>
                            ))}
                        </ul>

                        {invalidLine && (
                            <p className="mt-4 text-sm text-red-600">
                                Het aantal voor ‘{invalidLine.description}’ moet tussen 0 en{" "}
                                {invalidLine.remainingQuantity} liggen.
                            </p>
                        )}
                        {!nothingLeftToCredit && keptLines.length === 0 && (
                            <p className="mt-4 text-sm text-red-600">
                                Een creditfactuur heeft minstens één regel nodig.
                            </p>
                        )}
                        {nothingLeftToCredit && (
                            <p className="mt-4 text-sm text-gray-600">
                                Er valt niets meer te crediteren op deze factuur.
                            </p>
                        )}

                        <div className="mt-6 flex items-center justify-between border-t border-gray-100 pt-4">
                            <span className="text-sm text-gray-600">Totaal te crediteren (excl. btw)</span>
                            <span className="text-sm font-semibold text-gray-900">
                                − € {centsToLocalizedString(totalCents)}
                            </span>
                        </div>
                    </div>

                    <div className="flex gap-3">
                        <PrimaryButton
                            title={isSubmitting ? "Bezig..." : "Creditfactuur aanmaken"}
                            disabled={!canSubmit}
                            onClick={handleSubmit}
                        />
                        <SecondaryButton
                            title="Annuleren"
                            onClick={() => navigate(`/invoices/${invoiceId}`)}
                        />
                    </div>
                </div>
            )}
        </Shell>
    );
}
